import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CanvasToolbar from "@/components/campaignBuilder/design/CanvasToolbar";
import CropDialog from "@/components/campaignBuilder/design/CropDialog";
import CustomizeDrawer from "@/components/campaignBuilder/design/CustomizeDrawer";
import EmailLockTip from "@/components/campaignBuilder/design/EmailLockTip";
import EmailPreview from "@/components/campaignBuilder/design/EmailPreview";
import FpEmailTip from "@/components/campaignBuilder/design/FpEmailTip";
import FullscreenPreviewOverlay from "@/components/campaignBuilder/design/FullscreenPreviewOverlay";
import LightbulbBtn from "@/components/campaignBuilder/design/LightbulbBtn";
import LandingCustomizeDrawer from "@/components/campaignBuilder/design/landing/LandingCustomizeDrawer";
import LandingPreview from "@/components/campaignBuilder/design/landing/LandingPreview";
import StartHereTip from "@/components/campaignBuilder/design/StartHereTip";
import TutorialModal from "@/components/campaignBuilder/design/TutorialModal";
import UnboxingCustomizeDrawer from "@/components/campaignBuilder/design/unboxing/UnboxingCustomizeDrawer";
import { buildUbHeadline } from "@/components/campaignBuilder/design/unboxing/unboxing-helpers";
import GiftMessageModal from "@/components/giftExperience/GiftMessageModal";
import Unboxing from "@/components/giftExperience/Unboxing";
import { Confetti } from "@/components/redemption/experiential/Confetti";
import { primeConfettiAudio } from "@/components/redemption/experiential/confettiAudio";
import GiftVideoModal from "@/components/redemption/GiftVideoModal";
import { LANDING_CONFETTI_Z } from "@/constants/campaignBuilder";
import { resolveSchemeColors } from "@/constants/landing";
import { type CropTarget, useCampaignBuilder } from "@/context/CampaignBuilderContext";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { getStepPath } from "@/lib/campaigns";
import type { CanvasTab, DeviceMode } from "@/types/types";
import { isAnonymousLinks, isSelfSendMode } from "@/utils/campaignMode-helper";
import { resolveAllTokens, resolveUnboxingHeadline } from "@/utils/emailPreview-helper";
import { resolveUnboxingLogoUrl } from "@/utils/unboxing-helper";

export default function DesignStep() {
  const {
    stage3Email,
    setStage3Email,
    cropDialogTarget,
    closeCropDialog,
    recipients,
    stage3Unboxing,
    setStage3Unboxing,
    stage3Landing,
    setStage3Landing,
    designActiveTab,
    setDesignActiveTab,
    designDrawerOpen,
    setDesignDrawerOpen,
    emailConfigTab,
    setEmailConfigTab,
    landingConfigTab,
    setLandingConfigTab,
    saveStepIfDirty,
    isStepDirty,
    notificationMode,
    linkToList,
    senderFirstName,
    senderLastName,
    senderCompany,
    openRecipientsAccordion,
    campaignId,
  } = useCampaignBuilder();
  const navigate = useNavigate();

  // Manual send: GfG never emails, so the Email Design tab is locked and the design area defaults
  // to Unboxing (see the pivot effect below) instead of the locked email canvas.
  const unboxingLogoUrl = resolveUnboxingLogoUrl(stage3Email, stage3Landing, notificationMode);
  // Self-send (manual + universal): GfG never emails, so the Email Design tab is locked and the design
  // area defaults to Unboxing (see the pivot effect below) instead of the locked email canvas.
  const emailLocked = isSelfSendMode(notificationMode);

  // An ANONYMOUS-links campaign (manual Path A "No, just set a quantity", or any universal) has no
  // recipient rows, so its @Sender* / @Signature tokens resolve from the Step 2 "Who are these gifts
  // from?" fields. A manual "link to a list" campaign (Path B) has real recipient rows like gfg — leave
  // manualSender undefined so @FirstName / @LastName / @Sender* resolve from the first populated row
  // instead. (emailLocked stays true for every self-send mode: none sends a GfG email.)
  const manualSender = isAnonymousLinks(notificationMode, linkToList)
    ? { firstName: senderFirstName, lastName: senderLastName, company: senderCompany }
    : undefined;

  // Never sit on the locked Email tab — on entry in manual mode, or if the user flips to manual
  // while the Email canvas is active, pivot to Unboxing.
  useEffect(() => {
    if (emailLocked && designActiveTab === "email") setDesignActiveTab("unboxing");
  }, [emailLocked, designActiveTab, setDesignActiveTab]);
  // Sender tokens in the unboxing headline resolve against the first row that actually
  // filled each field, so one populated contact anywhere in Step 2 is enough for the
  // preview to show real data instead of a bare @Tag. A field no row filled stays a tag.
  const ubHeadline = resolveUnboxingHeadline(
    buildUbHeadline(stage3Unboxing),
    stage3Email.signatureHtml,
    recipients,
    manualSender,
  );

  // The gift message is personalizable as well, and the recipient reads it with its @tokens
  // already resolved (server-side, in RedemptionService) — so the preview resolves it too.
  const giftMessage = resolveAllTokens(
    stage3Unboxing.giftMessageText,
    stage3Email.signatureHtml,
    recipients,
    manualSender,
  );

  // Step 3 is the one AUTOSAVE step: design changes flush to the draft on a ~60s cadence while
  // this step is open. `saveStepIfDirty(3)` no-ops when nothing changed, so a quiet minute costs
  // nothing. Latest-ref so the interval isn't torn down/recreated as design state changes.
  const saveStep3Ref = useRef(saveStepIfDirty);
  saveStep3Ref.current = saveStepIfDirty;
  useEffect(() => {
    const id = window.setInterval(() => void saveStep3Ref.current(3), 60_000);
    return () => window.clearInterval(id);
  }, []);

  // Tab close / refresh with unsaved design changes → the browser's native "leave?" prompt.
  // In-app navigation away from Step 3 is guarded separately (the nav's unsaved-changes modal).
  const isStep3DirtyRef = useRef(isStepDirty);
  isStep3DirtyRef.current = isStepDirty;
  useEffect(() => {
    function onBeforeUnload(e: BeforeUnloadEvent) {
      if (isStep3DirtyRef.current(3)) {
        e.preventDefault();
        e.returnValue = "";
      }
    }
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, []);

  // Which slice + field the crop dialog is editing (email vs landing logo/banner).
  // Always crop from the pristine original so re-cropping never shrinks a crop-of-a-crop.
  // Fallback to the displayed URL for legacy rows saved before originals were tracked.
  const cropSource: Record<CropTarget, string> = {
    logo: stage3Email.logoOriginalUrl || stage3Email.logoUrl,
    banner: stage3Email.bannerOriginalUrl || stage3Email.bannerUrl,
    "landing-logo": stage3Landing.logoOriginalUrl || stage3Landing.logoUrl,
  };
  const cropImageUrl = cropDialogTarget ? cropSource[cropDialogTarget] : "";
  const isLogoCrop = cropDialogTarget === "logo" || cropDialogTarget === "landing-logo";

  // Store the crop as the displayed URL; keep the original as the crop source. For a legacy
  // image with no original yet, capture the current (pre-crop) URL as the original.
  function applyCrop(croppedUrl: string) {
    switch (cropDialogTarget) {
      case "logo":
        setStage3Email({
          logoUrl: croppedUrl,
          logoOriginalUrl: stage3Email.logoOriginalUrl || stage3Email.logoUrl,
        });
        break;
      case "banner":
        setStage3Email({
          bannerUrl: croppedUrl,
          bannerOriginalUrl: stage3Email.bannerOriginalUrl || stage3Email.bannerUrl,
        });
        break;
      case "landing-logo":
        setStage3Landing({
          logoUrl: croppedUrl,
          logoOriginalUrl: stage3Landing.logoOriginalUrl || stage3Landing.logoUrl,
        });
        break;
    }
    closeCropDialog();
  }

  const [deviceMode, setDeviceMode] = useState<DeviceMode>("desktop");
  // The inline preview simulates a device via the `deviceMode` toggle (phone
  // frame). The FULLSCREEN preview fills the real screen, so it should reflect the
  // actual viewport — matching what the recipient truly sees (the /g/:token route
  // keys off this same hook). Otherwise a mobile toggle renders the mobile stack
  // across a desktop screen.
  const isViewportMobile = useIsMobile();
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Gift Message Modal — owned at page level so backdrop covers full screen
  const [giftMsgOpen, setGiftMsgOpen] = useState(false);
  // Incrementing this triggers Unboxing to dissolve intro + play video
  const [ubPlayTrigger, setUbPlayTrigger] = useState(0);

  // Landing tab: hero video modal + the celebration confetti (the drawer sub-tab is
  // in context so a validation Fix can target it).
  const [lpVideoOpen, setLpVideoOpen] = useState(false);
  const [confettiTrigger, setConfettiTrigger] = useState(0);
  const [confettiColors, setConfettiColors] = useState<string[] | undefined>(undefined);
  const confettiPrevTabRef = useRef<CanvasTab>(designActiveTab);

  const fireConfetti = useCallback((schemeIndex: number | null) => {
    setConfettiColors(resolveSchemeColors(schemeIndex));
    setConfettiTrigger((t) => t + 1);
  }, []);

  // Tutorial tour state — auto-opens on first ever visit to Design step
  const [tourOpen, setTourOpen] = useState(false);
  // Post-tour "Start here" tip state; acknowledged flag prevents re-showing
  const [tipOpen, setTipOpen] = useState(false);
  const [tipAcknowledged, setTipAcknowledged] = useState(false);
  // Fullscreen email tip — shown once on first fullscreen entry
  const [fpEmailTipOpen, setFpEmailTipOpen] = useState(false);
  // Manual-send helper: clicking the locked Email Design tab surfaces this instead of switching.
  const [emailLockTipOpen, setEmailLockTipOpen] = useState(false);

  // "Change Now" — drop back to Step 2 and force-open the Recipient Notification section, where the
  // sender can switch off manual send and let GfG email again (mirrors the prototype's
  // routeToRecipientNotification).
  const handleChangeNotification = useCallback(() => {
    setEmailLockTipOpen(false);
    openRecipientsAccordion("notification");
    navigate(getStepPath(2));
  }, [navigate, openRecipientsAccordion]);

  useEffect(() => {
    if (!localStorage.getItem("gfgf_tour_seen")) {
      localStorage.setItem("gfgf_tour_seen", "1");
      setTourOpen(true);
    }
  }, []);

  // Fire the celebration confetti when the recipient "arrives" on the landing — i.e.
  // when the canvas switches TO the landing tab (mirrors the recipient unbox→landing
  // moment). Guarded by a ref so it fires on entry only, not on every settings change.
  // Only in the fullscreen preview: there the full-viewport burst IS the recipient's
  // screen. Inline, the burst would splash over the builder chrome, so we leave the
  // inline celebration to the drawer's per-scheme Preview buttons.
  useEffect(() => {
    const entered = designActiveTab === "landing" && confettiPrevTabRef.current !== "landing";
    confettiPrevTabRef.current = designActiveTab;
    if (entered && isFullscreen && stage3Landing.confettiEnabled) {
      fireConfetti(stage3Landing.confettiScheme);
    }
  }, [
    designActiveTab,
    isFullscreen,
    stage3Landing.confettiEnabled,
    stage3Landing.confettiScheme,
    fireConfetti,
  ]);

  const prevTabRef = useRef<CanvasTab>(designActiveTab);

  function handleEnterFullscreen() {
    prevTabRef.current = designActiveTab;
    // Manual send has no email — start the preview on Unboxing, and skip the email-specific tip.
    setDesignActiveTab(emailLocked ? "unboxing" : "email");
    setIsFullscreen(true);
    if (!emailLocked && !localStorage.getItem("gfgf_fp_email_tip_seen")) {
      setFpEmailTipOpen(true);
    }
  }

  function handleExitFullscreen() {
    setDesignActiveTab(prevTabRef.current);
    setIsFullscreen(false);
    setFpEmailTipOpen(false);
  }

  // Dashboard "Preview" enters here with `?preview=1` — auto-open the fullscreen recipient preview
  // once (the campaign is loaded on the builder's mount), then strip the flag so a manual exit
  // doesn't re-trigger it.
  const [searchParams, setSearchParams] = useSearchParams();
  const autoPreviewedRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once, ref-guarded; enters fullscreen from the nav flag then clears it.
  useEffect(() => {
    if (autoPreviewedRef.current || searchParams.get("preview") !== "1") return;
    autoPreviewedRef.current = true;
    handleEnterFullscreen();
    searchParams.delete("preview");
    setSearchParams(searchParams, { replace: true });
  }, []);

  function handleCustomizeToggle() {
    setDesignDrawerOpen(!designDrawerOpen);
  }

  return (
    // Fill the <main> area from CampaignBuilder.tsx — no max-width centering
    <div className="relative flex flex-col h-full bg-wax" style={{ height: "calc(100vh - 76px)" }}>
      {/* ── Canvas area (fills remaining space) ── */}
      <CanvasToolbar
        emailLocked={emailLocked}
        onLockedEmailClick={() => setEmailLockTipOpen(true)}
        activeTab={designActiveTab}
        onTabChange={setDesignActiveTab}
        deviceMode={deviceMode}
        onDeviceChange={setDeviceMode}
        drawerOpen={designDrawerOpen}
        onCustomizeToggle={handleCustomizeToggle}
        onEnterFullscreen={handleEnterFullscreen}
      />

      {/* Manual-send email-lock helper — anchored below the locked Email Design tab. */}
      <EmailLockTip
        open={emailLockTipOpen}
        onClose={() => setEmailLockTipOpen(false)}
        onChangeNow={handleChangeNotification}
      />

      {/* Helper signal — re-launches the guided tour */}
      <LightbulbBtn
        onClick={() => {
          setTourOpen(true);
          setTipOpen(false);
        }}
      />

      {/* Preview area — each tab owns its own container so padding/scroll can differ */}
      {designActiveTab === "email" && (
        <div className="flex-1 overflow-y-auto px-5 py-5 flex justify-center items-start bg-wax">
          <EmailPreview
            state={stage3Email}
            isMobile={deviceMode === "mobile"}
            recipients={recipients}
          />
        </div>
      )}

      {designActiveTab === "unboxing" && (
        <div
          className={`flex-1 overflow-hidden flex bg-wax ${
            deviceMode === "mobile" ? "justify-center items-start overflow-y-auto py-5" : ""
          }`}
        >
          <Unboxing
            isMobile={deviceMode === "mobile"}
            partnerLogoUrl={unboxingLogoUrl}
            partnerLogoAlt="Partner"
            headline={ubHeadline}
            showGiftMessageButton={stage3Unboxing.showGiftMessage}
            skipUnboxing={stage3Unboxing.skipUnboxing}
            customVideoUrl={stage3Unboxing.customVideoUrl || undefined}
            onReadMessage={() => setGiftMsgOpen(true)}
            playTrigger={ubPlayTrigger}
            giftMsgOpen={giftMsgOpen && deviceMode === "mobile"}
            giftMessage={giftMessage || undefined}
            onCloseMessage={() => setGiftMsgOpen(false)}
            onUnwrapMessage={() => {
              setGiftMsgOpen(false);
              setUbPlayTrigger((t) => t + 1);
            }}
            withSound
            onUnwrap={primeConfettiAudio}
          />
        </div>
      )}

      {designActiveTab === "landing" && (
        <div className="flex-1 overflow-y-auto bg-wax flex justify-center items-start">
          {deviceMode === "mobile" ? (
            <div className="my-5 w-97.5 shrink-0 overflow-hidden rounded-xl border border-card-border bg-white shadow-lg">
              <LandingPreview
                isMobile
                onReadMessage={() => setGiftMsgOpen(true)}
                onPlayVideo={() => setLpVideoOpen(true)}
              />
            </div>
          ) : (
            <div className="w-full">
              <LandingPreview
                isMobile={false}
                onReadMessage={() => setGiftMsgOpen(true)}
                onPlayVideo={() => setLpVideoOpen(true)}
              />
            </div>
          )}
        </div>
      )}

      {/* ── Crop dialog — rendered at page level to escape drawer stacking context ── */}
      {cropDialogTarget && cropImageUrl && (
        <CropDialog
          imageUrl={cropImageUrl}
          title={isLogoCrop ? "Crop Your Logo" : "Crop Your Banner"}
          campaignId={campaignId}
          mediaType={cropDialogTarget}
          onApply={applyCrop}
          onClose={closeCropDialog}
        />
      )}

      {/* ── Customize drawers — tab-conditional so only the active one slides in ── */}
      <CustomizeDrawer
        open={designDrawerOpen && designActiveTab === "email"}
        onClose={() => setDesignDrawerOpen(false)}
        emailState={stage3Email}
        onEmailChange={setStage3Email}
        activeTab={emailConfigTab}
        onTabChange={setEmailConfigTab}
      />
      <UnboxingCustomizeDrawer
        open={designDrawerOpen && designActiveTab === "unboxing"}
        onClose={() => setDesignDrawerOpen(false)}
        settings={stage3Unboxing}
        onSettingsChange={setStage3Unboxing}
      />
      <LandingCustomizeDrawer
        open={designDrawerOpen && designActiveTab === "landing"}
        onClose={() => setDesignDrawerOpen(false)}
        landingState={stage3Landing}
        onLandingChange={setStage3Landing}
        activeTab={landingConfigTab}
        onTabChange={setLandingConfigTab}
        onPreviewConfetti={fireConfetti}
      />

      {/* ── Fullscreen preview overlay ── */}
      {/* Step 4's RecipientExperienceCard + useGiftPreviewFlow.ts run the same stage machine on
          local state instead of designActiveTab — keep both in sync when changing either. */}
      {isFullscreen && (
        <FullscreenPreviewOverlay
          onExit={handleExitFullscreen}
          fillContent={designActiveTab === "unboxing"}
        >
          {designActiveTab === "email" && (
            <EmailPreview
              state={stage3Email}
              isMobile={isViewportMobile}
              recipients={recipients}
              onChooseGift={() => {
                setFpEmailTipOpen(false);
                setDesignActiveTab("unboxing");
              }}
            />
          )}
          {designActiveTab === "unboxing" && (
            <Unboxing
              isMobile={isViewportMobile}
              partnerLogoUrl={unboxingLogoUrl}
              partnerLogoAlt="Partner"
              headline={ubHeadline}
              showGiftMessageButton={stage3Unboxing.showGiftMessage}
              skipUnboxing={stage3Unboxing.skipUnboxing}
              customVideoUrl={stage3Unboxing.customVideoUrl || undefined}
              onReadMessage={() => setGiftMsgOpen(true)}
              playTrigger={ubPlayTrigger}
              onVideoEnd={() => setDesignActiveTab("landing")}
              isFullscreen
              withSound
              // Unwrap tap is the only user gesture here — unlocks the confetti audio for the later timer-driven play.
              onUnwrap={primeConfettiAudio}
            />
          )}
          {designActiveTab === "landing" && (
            <div className="w-full">
              <LandingPreview
                isMobile={isViewportMobile}
                onReadMessage={() => setGiftMsgOpen(true)}
                onPlayVideo={() => setLpVideoOpen(true)}
                isFullscreen
              />
            </div>
          )}
        </FullscreenPreviewOverlay>
      )}
      {/* ── Gift Message Modal — owned at page level. On the Unboxing tab it carries the
          Unwrap CTA (desktop only; mobile renders inside Unboxing). On the Landing tab
          the gift is already unwrapped, so it's message-only, shown on any device. ── */}
      {giftMsgOpen && !(designActiveTab === "unboxing" && deviceMode === "mobile") && (
        <GiftMessageModal
          message={giftMessage || undefined}
          onClose={() => setGiftMsgOpen(false)}
          onUnwrap={
            designActiveTab === "unboxing"
              ? () => {
                  setGiftMsgOpen(false);
                  setUbPlayTrigger((t) => t + 1);
                }
              : undefined
          }
        />
      )}

      {/* ── Landing hero video modal ── */}
      <GiftVideoModal
        open={lpVideoOpen}
        videoUrl={stage3Landing.videoUrl || undefined}
        onClose={() => setLpVideoOpen(false)}
      />

      {/* ── Landing celebration confetti — only mounted on the Landing tab (the shape
          pills / scheme Preview + the fullscreen landing-arrival fire it). Raised above
          the fullscreen overlay so it shows there too; the mount-time trigger guard keeps
          switching to the tab from re-firing on its own. ── */}
      {designActiveTab === "landing" && (
        <Confetti
          trigger={confettiTrigger}
          shape={stage3Landing.confettiShape}
          colors={confettiColors}
          zIndex={LANDING_CONFETTI_Z}
        />
      )}

      {/* ── Fullscreen email tip — slides in from left on first fullscreen entry ── */}
      <FpEmailTip
        open={fpEmailTipOpen}
        onGotIt={() => {
          localStorage.setItem("gfgf_fp_email_tip_seen", "1");
          setFpEmailTipOpen(false);
        }}
      />

      {/* ── Guided tour overlay ── */}
      <TutorialModal
        open={tourOpen}
        onClose={() => setTourOpen(false)}
        onTabChange={setDesignActiveTab}
        onTourComplete={() => {
          if (!tipAcknowledged) setTipOpen(true);
        }}
      />

      {/* ── Post-tour "Start here" tip — slides in after the tour completes ── */}
      <StartHereTip
        open={tipOpen}
        onClose={() => setTipOpen(false)}
        onGotIt={() => {
          setTipAcknowledged(true);
          setTipOpen(false);
        }}
      />
    </div>
  );
}
